import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Grid } from "@mui/material";
import { ActionMenu, Button, Dialog, GenericTable, tCell, toastMessage } from "../utils";
import { useDebounce, useDynamicModuleEntries, useTemplateDetail, useUserDetail, useUserPermissions } from "../../services/hooks";
import { buttonClasses } from "../utils/theme";
import { capitalizeCamelCase, fakeFileInput, findDeepValue, parseUnderscore } from "../../services/functions";
import { formatDate as formatDateWithTimezone } from "../../services/functions";
import { useCreateDynamicModuleEntryMutation, useUpdateDynamicModuleEntryMutation, useDeleteDynamicModuleEntryMutation, useGetDynamicModuleEntryMutation } from "../../api/vmsSlice";
import DynamicFormRender from "../Dynamic/DynamicForm";
import { usePlantContext } from "../../context/plantContext";
import { CrossIcon, TickBoxIcon } from "../../services/icons";
import dayjs from "dayjs";
import { downloadExcel } from "react-export-table-to-excel";

// const SAMPLE_EXCEL_PATH = "/sample_excels/supplier_sample.xlsx";
const EXCEL_TYPE = "supplier";
const defaultFilter = {
	pagesize: 10,
	page_no: 1,
};
const MAX_EXCEL_DOWNLOAD_ROWS = 15000;
const dataIconMap = {
	"true": <TickBoxIcon sx={{ fill: "green" }} />,
	"false": <CrossIcon sx={{ fill: "red" }} />
};
const excelValueMap = {
	"true": "Yes",
	"false": "No",
};
const formatBoolValue = (value) => {
	if (value === true || value === "true") return "Yes";
	if (value === false || value === "false") return "No";
	return "-";
};
export default function DynamicModule() {
	const { plantId, timezone } = usePlantContext();
	const formatDate = (date, format) => formatDateWithTimezone(date, format, timezone);
	const location = useLocation();
	const { data: currentModule = {} } = location.state || {};
	const { moduleName = "", hasExcelUpload = false, templateId = "", orgId } = currentModule;
	const { isSuperAdmin } = useUserDetail();
	const { userPermissions, isFetched } = useUserPermissions();


	const canAdd = isSuperAdmin || userPermissions[`module-add${moduleName.replaceAll(" ", "")}`]?.value;
	const canGet = isSuperAdmin || userPermissions[`module-get${moduleName.replaceAll(" ", "")}`]?.value;
	const canUpdate = isSuperAdmin || userPermissions[`module-update${moduleName.replaceAll(" ", "")}`]?.value;
	const canDelete = isSuperAdmin || userPermissions[`module-delete${moduleName.replaceAll(" ", "")}`]?.value;

	const [isModal, setIsModal] = useState(false);
	const updateDataRef = useRef(null);

	const filterRef = useRef(defaultFilter);
	const { page_no, pagesize } = filterRef.current;

	const { dynamicModuleEntries, count, isLoading, getDynamicModuleEntries, tableHeader } = useDynamicModuleEntries();
	const [getDynamicModuleEntriesForDownload, { isLoading: isExcelDownloading }] = useGetDynamicModuleEntryMutation();
	const { templateDetail, getTemplateDetail } = useTemplateDetail(null, "dynamicModuleFilters");
	const primaryFilters = useMemo(() => {
		const primaryFilters = [];
		for (let attr of templateDetail?.attributes || []) {
			let _attr = { ...attr, required: false };
			if (attr.isPrimaryFilter) {
				primaryFilters.push(_attr);
			}
		}
		return primaryFilters;
	}, [templateDetail]);

	useLayoutEffect(() => {
		filterRef.current = { ...filterRef.current, templateId, orgId, moduleName, ...(plantId ? { plantIds: plantId } : {})};
		fetchData()
		getTemplateDetail({ id: templateId, onlyFilterFields: true });

	}, [templateId, orgId, moduleName, plantId]);
	const onModalOpen = () => setIsModal(true);
	const onModalClose = () => {
		setIsModal(false);
		if (updateDataRef.current) {
			updateDataRef.current = null;
		}
	};
	const fetchData = () => {
		getDynamicModuleEntries(filterRef.current);
	};
	const clearDynamicFilters = () => {
		filterRef.current = {
			templateId,
			orgId,
			moduleName,
			...(plantId ? { plantIds: plantId } : {}),
			page_no: defaultFilter.page_no,
			pagesize: defaultFilter.pagesize,
		};
		fetchData();
	};

	const [handleDelete] = useDeleteDynamicModuleEntryMutation();
	let actionHandlers = {};
	if (canUpdate) {
		actionHandlers.edit = (id) => {
			updateDataRef.current = dynamicModuleEntries[id];
			// console.log(updateDataRef.current, dynamicModuleEntries , id)
			setIsModal(true);
		};
	}
	if (canDelete) {
		actionHandlers.delete = async (id) => {
			// console.log(id, "delete");
			const data = dynamicModuleEntries[id];
			const payload = { deleteId: data._id, moduleName };
			const {
				data: { error, message },
			} = await handleDelete(payload);
			if (error) {
				toastMessage(false, message);
			} else {
				fetchData();
				toastMessage(true, message);
			}
		};
	}
	let header = ["S.no", ...tableHeader.map((_) => _.label)];
	if (isSuperAdmin) {
		header.push("Org Name");
	}
	if (canUpdate || canDelete) {
		header.push("Action");
	}
	const rows =
		dynamicModuleEntries?.map((d, i) => {
			let row = [];
			row.push(tCell((page_no - 1) * pagesize + i + 1));

			for (let _ of tableHeader) {
				let value = findDeepValue(d, _.name.split("."));
				if (_.type == "icon") {
					value = dataIconMap[String(value)] || value;
				} else if (_.type == "bool") {
					value = formatBoolValue(value)
				} else if (_.type == "date") {
					value = formatDate(value)
				}
				row.push(tCell(value));
			}
			if (isSuperAdmin) {
				row.push(tCell(d.orgName));
			}
			if (canUpdate || canDelete) {
				row.push(<ActionMenu id={i} handlers={actionHandlers} />);
			}

			return row;
		}) || [];
	const onPageChange = (page_no) => {
		filterRef.current = { ...filterRef.current, page_no: page_no + 1 };
		fetchData()
	};
	const onPageSizeChange = (e) => {
		const { value } = e.target;
		filterRef.current = { ...filterRef.current, pagesize: value, page_no: 1 };
		fetchData()
	};
	const onBulkExcelUpload = () => {
		fakeFileInput(async (formData) => {
			formData.append("type", EXCEL_TYPE);
			// const payload = {
			// 	file,
			// 	type: EXCEL_TYPE,
			// };

			// const {
			// 	data: { error, message },
			// } = await handleUploadExcel(formData);
			// if (error) {
			// 	toastMessage(false, message);
			// } else {
			// 	toastMessage(true, message);
			// 	fetchData();
			// }
		});
	};

	const getExcelValue = (row, column) => {
		let value = findDeepValue(row, column.name.split("."));
		if (column.type == "icon") {
			value = excelValueMap[String(value)] || value;
		} else if (column.type == "bool") {
			value = formatBoolValue(value);
		} else if (column.type == "date") {
			value = formatDate(value);
		}

		if (value === undefined || value === null || value === "") {
			return "-";
		}
		if (Array.isArray(value)) {
			return value.map((item) => (typeof item === "object" ? JSON.stringify(item) : item)).join(", ");
		}
		if (typeof value === "object") {
			return JSON.stringify(value);
		}
		return value;
	};

	const onDownloadExcel = async () => {
		const totalRecords = count || dynamicModuleEntries?.length || 0;
		if (totalRecords > MAX_EXCEL_DOWNLOAD_ROWS) {
			toastMessage(false, "Large amount of data, please shorten the date duration.");
			return;
		}
		if (!tableHeader.length) {
			toastMessage(false, "No columns available to download.");
			return;
		}

		try {
			const payload = {
				...filterRef.current,
				page_no: 1,
				pagesize: MAX_EXCEL_DOWNLOAD_ROWS,
			};
			const result = await getDynamicModuleEntriesForDownload(payload);
			const response = result?.data || {};
			const fullData = Array.isArray(response) ? response : response.data || [];
			const responseCount = response.count || fullData.length;

			if (responseCount > MAX_EXCEL_DOWNLOAD_ROWS) {
				toastMessage(false, "Large amount of data, please shorten the date duration.");
				return;
			}
			if (!fullData.length) {
				toastMessage(false, "No data found for selected filters.");
				return;
			}

			const headers = ["S.no", ...tableHeader.map((_) => _.label)];
			if (isSuperAdmin) {
				headers.push("Org Name");
			}

			const rows = fullData.map((row, index) => {
				const excelRow = [index + 1, ...tableHeader.map((column) => getExcelValue(row, column))];
				if (isSuperAdmin) {
					excelRow.push(row.orgName || "-");
				}
				return excelRow;
			});

			const fileName = `${moduleName || "dynamic_module"}`.replace(/\s+/g, "_");
			downloadExcel({
				fileName,
				sheet: fileName,
				tablePayload: {
					header: headers,
					body: rows,
				},
			});
			toastMessage(true, "Excel report downloaded successfully!");
		} catch (error) {
			console.error("Dynamic module excel download error:", error);
			toastMessage(false, "Failed to download Excel report. Please try again.");
		}
	};

	const debouncedFetchData = useDebounce(fetchData, 500);
	const onFilterChange_dynamic = useMemo(
		() => (name, value, type) => {
			if (type == "clear") {
				filterRef.current = {
					templateId,
					orgId,
					moduleName,
					...(plantId ? { plantIds: plantId } : {}),
				}
			} else {
				let _value = value;
				if (name == "start" && value) {
					if (value) {
						const newStartDate = dayjs(value).startOf("day").toDate().toISOString();
						_value = newStartDate;
					} else {
						_value = undefined;
					}
				}
				if (name == "end") {
					if (value) {
						const newEndDate = dayjs(value).endOf("day").toDate().toISOString();
						_value = newEndDate;
					} else {
						_value = undefined;
					}
				}
				filterRef.current = { ...filterRef.current, [name]: _value };
				if (_value === "") {
					delete filterRef.current[name]
				}
			}
			filterRef.current.page_no=1;
			filterRef.current.pagesize=10;
			debouncedFetchData()
		},
		[templateId, orgId, moduleName, plantId, debouncedFetchData],
	);
	if (isFetched && !canGet) return <Navigate to="/not-authorized" replace />;
	return (
		<>
			<div className="contentpanel">
				<Grid container spacing="0.5rem" mb={2}>
					<Grid item xl={6} lg={6} md={5} sm={1} xs={12}>
						<h1 className="title">{parseUnderscore(capitalizeCamelCase(moduleName))}</h1>
					</Grid>
					<Grid item xl={6} lg={6} md={7} sm={1} xs={12}>
						<Grid container spacing="0.5rem" justifyContent={"flex-end"}>
							{canAdd ? (
								<Grid item xl={4} lg={4} md={4} sm={4} xs={12}>
									<Button onClick={onModalOpen} text={`Add ${capitalizeCamelCase(moduleName)}`} style={buttonClasses.lynkitOrangeEmpty} />
								</Grid>
							) : null}
							{/* {canAdd ? (
								<Grid item xl={4} lg={4} md={4} sm={4} xs={12}>
									<Button onClick={onBulkExcelUpload} text="Bulk Upload" style={buttonClasses.lynkitOrangeFill} />
								</Grid>
							) : null}
							{canAdd ? (
								<Grid item xl={4} lg={4} md={4} sm={4} xs={12}>
									<a href={SAMPLE_EXCEL_PATH} download>
										<Button text="Download Sample" style={buttonClasses.lynkitBlackFill} />
									</a>
								</Grid>
							) : null} */}
						</Grid>
					</Grid>
				</Grid>
				<hr className="bgg" />

				{templateDetail?.attributes?.length ? (
					<>
						<DynamicFormRender
							previewAttributes={primaryFilters}
							onFilterChange={onFilterChange_dynamic}
							filterOnly
							noBtn
							renderAction={(onClear) => {
								return (
									<div
										style={{
											display: "flex",
											gap: "1rem",
											alignItems: "center",
											// justifyContent: "center",
										}}
									>
										<Button onClick={() => {
											onClear();
											clearDynamicFilters();
										}} text={"Clear"} style={{ ...buttonClasses.lynkitOrangeEmpty, width: "fit-content", height: "38px", minHeight: "38px", marginTop: "-8px" }} />
										<Button onClick={onDownloadExcel} disabled={isExcelDownloading} text={isExcelDownloading ? "Downloading..." : "Download Excel"} style={{ ...buttonClasses.lynkitOrangeFill, width: "fit-content", height: "38px", minHeight: "38px", marginTop: "-8px" }}/>
									</div>
								);
							}}
							plantId={plantId}
						/>
					</>
				) : null}

				<GenericTable
					header={header}
					rows={rows}
					pageCount={count || dynamicModuleEntries?.length}
					pageNo={page_no}
					limit={pagesize}
					onPageChange={onPageChange}
					onPageSizeChange={onPageSizeChange}
					isLoading={isLoading}
				/>
				{isModal && <AddForm open={isModal} onClose={onModalClose} updateData={updateDataRef.current} onUpdateSuccess={fetchData} />}
			</div>
		</>
	);
}

function AddForm({ onClose, open, updateData, onUpdateSuccess }) {
	const location = useLocation();
	const { data: currentModule = {} } = location.state || {};
	const { plantId, plantName } = usePlantContext();

	const { moduleName = "", templateId = "", orgId = "", orgName = "", templateName = "" } = currentModule;

	const [handleAdd, { isLoading: isAdding }] = useCreateDynamicModuleEntryMutation();
	const [handleUpdate, { isLoading: isUpdating }] = useUpdateDynamicModuleEntryMutation();
	const handleFormSubmit = async (formData) => {
		const { data } = formData;
		let payload = { data, moduleName, templateId, orgId, orgName, plantId, plantName };
		if (updateData) {
			payload.updateId = updateData._id;
		}
		// console.log(payload);
		const {
			data: { error, message },
		} = updateData ? await handleUpdate(payload) : await handleAdd(payload);
		if (error) {
			toastMessage(false, message);
		} else {
			toastMessage(true, message);
			onUpdateSuccess();
			onClose();
		}
	};
	// console.log(updateData);
	return (
		<Dialog size="md" open={open} handleClose={onClose} title={`${updateData ? "Update" : "Add"} ${moduleName}`} noBtn>
			<DynamicFormRender
				templateId={templateId}
				templatePayload={{
					id: templateId,
					organization_id: orgId,
					isUpdate: !!updateData,
				}}
				onClose={onClose}
				onSubmit={handleFormSubmit}
				isSubmitDisabled={isAdding || isUpdating}
				updateData={updateData}
			/>
		</Dialog>
	);
}
